import { useEffect, useState, useCallback } from "react";
import { theme, card, inputStyle } from "../theme";
import { api, apiErrorMessage } from "../api/client";
import { useCurrentUser } from "../auth/useCurrentUser";
import { safeHref, openSafe } from "../utils/safeHref";
import { EvidenceImportPanel } from "../components/EvidenceImportPanel";

import { cbSafe } from "../lib/colourBlind";
const FEATURES = [
  {
    title: "Generated policy documents",
    description: "Every policy document you generate will automatically appear in your dedicated OneDrive folder, ready to share with auditors.",
  },
  {
    title: "Uploaded evidence documents",
    description: "Evidence files you upload to the platform are mirrored to OneDrive so your compliance folder stays complete and up to date.",
  },
  {
    title: "Dedicated compliance folder",
    description: "All files land in a single named folder in your OneDrive, keeping your compliance artefacts organised and separate.",
  },
  {
    title: "Outlook calendar and Teams",
    description: "Once connected you can also put your calendar tasks and register deadlines in Outlook, and send alerts, a daily digest and a test message to a Teams channel.",
  },
  {
    title: "Automatic sync on every change",
    description: "There is nothing to remember — every time a document is generated or uploaded the sync happens silently in the background.",
  },
];

interface M365Features {
  calendarEnabled: boolean;
  teamsNotificationsEnabled: boolean;
  teamsWebhookUrl: string | null;
  teamsAiAdviceEnabled: boolean;
  /** "connector" = a legacy Office 365 Connector URL (being retired by Microsoft); null = none or not recognised. */
  teamsWebhookKind?: "workflow" | "connector" | null;
  /** Outcome of the last post to the channel (lib/teamsNotify.ts). */
  teamsHealth?: { lastDeliveredAt: string | null; lastError: string | null; lastErrorAt: string | null };
  /** Register deadlines currently mirrored into Outlook (services/deadlineCalendar.ts). */
  calendarDeadlineEvents?: number;
}

function when(iso: string | null): string {
  return iso ? new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "";
}

interface M365Status {
  connected: boolean;
  userEmail: string | null;
  folderWebUrl: string | null;
  folderName: string | null;
  /** Set while Microsoft rejects the stored sign-in: still connected on paper, unusable until re-authorised. */
  authError: { message: string; at: string | null } | null;
  features: M365Features;
}

export function ConnectM365Page() {
  const [status, setStatus] = useState<M365Status | null>(null);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [banner, setBanner] = useState<{ kind: "success" | "error"; text: string } | null>(null);
  const [folderName, setFolderName] = useState("Abacas Security Compliance Platform");
  const [renaming, setRenaming] = useState(false);
  const [renameDraft, setRenameDraft] = useState("");
  const [renameLoading, setRenameLoading] = useState(false);
  const [togglingFeature, setTogglingFeature] = useState<string | null>(null);
  const [webhookUrl, setWebhookUrl] = useState("");
  const [savingWebhook, setSavingWebhook] = useState(false);
  const [calSyncing, setCalSyncing] = useState(false);
  const [testingTeams, setTestingTeams] = useState(false);
  const { user } = useCurrentUser();
  // POST /m365/calendar-sync is client-only behind integrations:admin.
  const canAdmin = user?.role === "client" && !!user.permissions?.includes("integrations:admin");

  // Parse query params from the OAuth callback redirect.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("connected") === "true") {
      setBanner({ kind: "success", text: "Microsoft 365 connected successfully!" });
      window.history.replaceState({}, "", window.location.pathname);
      const t = setTimeout(() => setBanner(null), 4000);
      return () => clearTimeout(t);
    }
    const error = params.get("error");
    if (error === "invalid_state") {
      setBanner({ kind: "error", text: "Connection failed — the link expired. Please try again." });
      window.history.replaceState({}, "", window.location.pathname);
    } else if (error === "auth_failed") {
      setBanner({ kind: "error", text: "Microsoft did not authorise the connection. Please try again — if it keeps failing, ask your administrator to check the app's consent for your account." });
      window.history.replaceState({}, "", window.location.pathname);
    } else if (error === "connect_failed") {
      setBanner({ kind: "error", text: "Microsoft authorised the connection but it could not be saved. Please try again." });
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  const fetchStatus = useCallback(async () => {
    setLoadingStatus(true);
    try {
      const data = await api.get<M365Status>("/m365/status");
      setStatus(data);
      if (data.folderName) setFolderName(data.folderName);
      if (data.features.teamsWebhookUrl) setWebhookUrl(data.features.teamsWebhookUrl);
    } catch {
      // Non-fatal — leave status null.
    } finally {
      setLoadingStatus(false);
    }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  async function handleToggleFeature(key: keyof M365Features, current: boolean) {
    setTogglingFeature(key);
    try {
      await api.patch("/m365/settings", { [key]: !current });
      setStatus((s) => s ? { ...s, features: { ...s.features, [key]: !current } } : s);
    } catch {
      setBanner({ kind: "error", text: "Failed to update setting. Please try again." });
    } finally {
      setTogglingFeature(null);
    }
  }

  async function handleSaveWebhookUrl() {
    setSavingWebhook(true);
    try {
      await api.patch("/m365/settings", { teamsWebhookUrl: webhookUrl.trim() || null });
      await fetchStatus();
      setBanner({ kind: "success", text: webhookUrl.trim() ? "Webhook URL saved. Send a test message to check it reaches the channel." : "Webhook URL cleared." });
    } catch (e) {
      setBanner({ kind: "error", text: apiErrorMessage(e, "Failed to save webhook URL. Please try again.") });
    } finally {
      setSavingWebhook(false);
    }
  }

  async function handleTestTeams() {
    setTestingTeams(true);
    try {
      await api.post("/m365/teams/test", {});
      setBanner({ kind: "success", text: "Test message delivered. Check your Teams channel." });
    } catch (e) {
      setBanner({ kind: "error", text: apiErrorMessage(e, "Teams did not accept the test message.") });
    } finally {
      setTestingTeams(false);
      fetchStatus();
    }
  }

  async function handleConnect() {
    setConnecting(true);
    try {
      const params = new URLSearchParams({ folderName });
      const data = await api.get<{ url: string }>(`/m365/auth-url?${params.toString()}`);
      window.location.href = data.url;
    } catch {
      setBanner({ kind: "error", text: "Failed to start Microsoft 365 connection. Please try again." });
      setConnecting(false);
    }
  }

  async function handleDisconnect() {
    setDisconnecting(true);
    try {
      await api.del("/m365/disconnect");
      await fetchStatus();
      setBanner({ kind: "success", text: "Microsoft 365 disconnected." });
    } catch {
      setBanner({ kind: "error", text: "Failed to disconnect. Please try again." });
    } finally {
      setDisconnecting(false);
    }
  }

  async function handleRename() {
    if (!renameDraft.trim() || renameDraft.trim() === status?.folderName) {
      setRenaming(false);
      return;
    }
    setRenameLoading(true);
    try {
      const data = await api.patch<{ folderName: string; folderWebUrl: string }>("/m365/folder", { folderName: renameDraft.trim() });
      setStatus((s) => s ? { ...s, folderName: data.folderName, folderWebUrl: data.folderWebUrl } : s);
      setFolderName(data.folderName);
      setBanner({ kind: "success", text: `Folder renamed to "${data.folderName}".` });
      setRenaming(false);
    } catch {
      setBanner({ kind: "error", text: "Failed to rename folder. Please try again." });
    } finally {
      setRenameLoading(false);
    }
  }

  async function handleCalendarSync() {
    setCalSyncing(true);
    try {
      const data = await api.post<{ synced: number }>("/m365/calendar-sync", {});
      setBanner({ kind: "success", text: `${data.synced === 0 ? "Calendar tasks are already in Outlook." : `${data.synced} calendar task${data.synced === 1 ? "" : "s"} added to Outlook.`} Register deadlines are being refreshed in the background.` });
    } catch (e) {
      setBanner({ kind: "error", text: apiErrorMessage(e, "Calendar sync failed.") });
    } finally {
      setCalSyncing(false);
    }
  }

  const btnPrimary: React.CSSProperties = {
    border: `1px solid ${theme.color.accent}`,
    background: theme.color.accentBg,
    borderRadius: 3,
    padding: "8px 16px",
    fontSize: 12,
    fontWeight: 700,
    color: theme.color.accentText,
    cursor: "pointer",
  };

  return (
    <div className="page-pad">
      <h1 style={{ margin: "0 0 6px", fontSize: 20, fontWeight: 700, color: theme.color.heading, letterSpacing: "-0.3px" }}>
        Connect Microsoft 365
      </h1>
      <p style={{ margin: "0 0 24px", fontSize: 13, color: theme.color.subtle }}>
        Connect your Microsoft 365 account to sync compliance documents to OneDrive, mirror schedule events to Outlook, and enable Teams notifications and a daily compliance tip.
      </p>

      {/* Banner */}
      {banner && (
        <div style={{
          ...card, marginBottom: 20,
          border: `1px solid ${banner.kind === "success" ? theme.color.success : theme.color.danger}`,
          background: banner.kind === "success" ? cbSafe("#16a34a10") : cbSafe("#dc262610"),
          display: "flex", justifyContent: "space-between", alignItems: "center",
        }}>
          <span style={{ fontSize: 13, color: banner.kind === "success" ? theme.color.successText : theme.color.dangerText }}>
            {banner.text}
          </span>
          <button onClick={() => setBanner(null)} style={{ background: "none", border: "none", color: theme.color.muted, cursor: "pointer", fontSize: 16 }}>×</button>
        </div>
      )}

      {loadingStatus && (
        <div style={{ ...card, color: theme.color.subtle, fontSize: 13 }}>Loading connection status…</div>
      )}

      {/* ── Connected state ── */}
      {!loadingStatus && status?.connected && (
        <>
          <EvidenceImportPanel compact sourceFilter={["m365"]} />

          {/* Status + disconnect — amber while Microsoft rejects the stored sign-in */}
          <div style={{
            ...card, marginBottom: 16,
            border: `1px solid ${status.authError ? theme.color.warning : theme.color.success}`,
            background: status.authError ? theme.color.warningBg : cbSafe("#16a34a0a"),
          }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{
                  fontSize: 12, fontWeight: 700,
                  color: status.authError ? theme.color.warningText : theme.color.successText,
                  background: status.authError ? "transparent" : cbSafe("#16a34a20"),
                  border: `1px solid ${status.authError ? theme.color.warning : theme.color.success}`,
                  borderRadius: 3, padding: "3px 10px",
                }}>
                  {status.authError ? "Needs re-authorising" : "Connected"}
                </span>
                <span style={{ fontSize: 13, color: theme.color.muted }}>{status.userEmail}</span>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                {status.authError && (
                  <button onClick={handleConnect} disabled={connecting} style={{ ...btnPrimary, opacity: connecting ? 0.6 : 1 }}>
                    {connecting ? "Redirecting…" : "Reconnect"}
                  </button>
                )}
                <button
                  onClick={handleDisconnect}
                  disabled={disconnecting}
                  style={{ background: "transparent", border: cbSafe("1px solid #7f1d1d"), borderRadius: 3, padding: "7px 14px", fontSize: 12, fontWeight: 600, color: theme.color.dangerText, cursor: disconnecting ? "not-allowed" : "pointer", opacity: disconnecting ? 0.6 : 1 }}
                >
                  {disconnecting ? "Disconnecting…" : "Disconnect"}
                </button>
              </div>
            </div>
            {status.authError && (
              <div style={{ marginTop: 10, fontSize: 12, color: theme.color.warningText, lineHeight: 1.6 }}>
                Microsoft is rejecting the stored sign-in, so documents cannot be mirrored, imported or synced to the calendar until you reconnect
                {status.authError.at ? ` (since ${new Date(status.authError.at).toLocaleString()})` : ""}.
                <div style={{ marginTop: 4, color: theme.color.muted, fontVariantNumeric: "tabular-nums", fontSize: 11, wordBreak: "break-word" }}>{status.authError.message}</div>
              </div>
            )}
          </div>

          {/* Folder name + rename */}
          <div style={{ ...card, marginBottom: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: theme.color.heading, marginBottom: 10 }}>Compliance folder</div>
            {!renaming ? (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                <div>
                  <div style={{ fontSize: 13, color: theme.color.heading, fontWeight: 600 }}>{status.folderName || "Abacas Security Compliance Platform"}</div>
                  {status.folderWebUrl && (
                    <div
                      style={{ fontSize: 12, color: theme.color.accentText, cursor: "pointer", marginTop: 2, textDecoration: "underline" }}
                      onClick={() => openSafe(status.folderWebUrl)}
                    >
                      Open in OneDrive →
                    </div>
                  )}
                </div>
                <button
                  onClick={() => { setRenameDraft(status.folderName || "Abacas Security Compliance Platform"); setRenaming(true); }}
                  style={{ ...btnPrimary, padding: "6px 12px", fontWeight: 600 }}
                >
                  Rename folder
                </button>
              </div>
            ) : (
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <input
                  style={{ ...inputStyle, flex: 1, minWidth: 200 }}
                  value={renameDraft}
                  onChange={(e) => setRenameDraft(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") handleRename(); if (e.key === "Escape") setRenaming(false); }}
                  autoFocus
                />
                <button onClick={handleRename} disabled={renameLoading || !renameDraft.trim()} style={{ ...btnPrimary, opacity: renameLoading ? 0.6 : 1 }}>
                  {renameLoading ? "Renaming…" : "Save"}
                </button>
                <button onClick={() => setRenaming(false)} style={{ background: "transparent", border: `1px solid ${theme.color.borderStrong}`, borderRadius: 3, padding: "8px 12px", fontSize: 12, color: theme.color.muted, cursor: "pointer" }}>
                  Cancel
                </button>
              </div>
            )}
          </div>

          {/* Feature toggles */}
          <div style={{ ...card, padding: 0, overflow: "hidden" }}>
            <div style={{ padding: "14px 18px 10px", borderBottom: `1px solid ${theme.color.border}` }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: theme.color.heading }}>Integration features</div>
              <div style={{ fontSize: 12, color: theme.color.subtle, marginTop: 2 }}>Enable or disable each component independently.</div>
            </div>

            {([
              {
                key: "calendarEnabled" as keyof M365Features,
                label: "Outlook calendar sync",
                description: "Calendar tasks, audit bookings and confirmed consultant time are added to the connected account's Outlook calendar, together with every dated obligation from your registers: regulatory submission and incident notification deadlines, data subject request due dates, corrective actions, vulnerability remediation deadlines, supplier contract notice and end dates, certificate and insurance expiries, and policy, business impact and AI impact reviews. They are updated or removed when the record changes.",
                comingSoon: false,
              },
              {
                key: "teamsNotificationsEnabled" as keyof M365Features,
                label: "Teams notifications",
                description: "Everything that reaches My Actions is posted to your Teams channel: urgent items within the hour, everything else in a daily digest at 08:00 UTC with what falls due in the next seven days. Incidents, DORA alerts, vulnerability exposures, remediation, returned supplier questionnaires and AI check results are posted as they happen.",
                comingSoon: false,
              },
              {
                key: "teamsAiAdviceEnabled" as keyof M365Features,
                label: "Teams daily tip",
                description: "A compliance tip is posted to your Teams channel each morning: one open gap a day, with what your last compliance check recommended for it.",
                comingSoon: false,
              },
            ] as const).map((feature, i, arr) => {
              const enabled = status!.features[feature.key as keyof M365Features] as boolean;
              const toggling = togglingFeature === feature.key;
              const isTeamsNotif = feature.key === "teamsNotificationsEnabled";
              return (
                <div key={feature.key}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      justifyContent: "space-between",
                      gap: 16,
                      padding: "14px 18px",
                      borderBottom: (isTeamsNotif && enabled) || i < arr.length - 1 ? `1px solid ${theme.color.border}` : "none",
                      opacity: feature.comingSoon ? 0.65 : 1,
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 3 }}>
                        <span style={{ fontSize: 13, fontWeight: 600, color: theme.color.heading }}>{feature.label}</span>
                        {feature.comingSoon && (
                          <span style={{ fontSize: 10, fontWeight: 700, color: theme.color.accentText, background: theme.color.accentBg, border: `1px solid ${theme.color.accent}`, borderRadius: 4, padding: "1px 6px", letterSpacing: "0.04em" }}>
                            COMING SOON
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: 12, color: theme.color.subtle, lineHeight: 1.6 }}>{feature.description}</div>
                      {feature.key === "calendarEnabled" && enabled && (status!.features.calendarDeadlineEvents ?? 0) > 0 && (
                        <div style={{ fontSize: 11, color: theme.color.faint, marginTop: 4 }}>
                          {status!.features.calendarDeadlineEvents} register deadline{status!.features.calendarDeadlineEvents === 1 ? "" : "s"} in Outlook.
                        </div>
                      )}
                      {feature.key === "calendarEnabled" && enabled && canAdmin && (
                        <button onClick={handleCalendarSync} disabled={calSyncing}
                          style={{ ...btnPrimary, marginTop: 8, padding: "5px 12px", opacity: calSyncing ? 0.6 : 1 }}>
                          {calSyncing ? "Syncing…" : "Sync now"}
                        </button>
                      )}
                    </div>
                    <button
                      disabled={feature.comingSoon || toggling}
                      onClick={() => handleToggleFeature(feature.key, enabled)}
                      style={{
                        flexShrink: 0,
                        width: 44,
                        height: 24,
                        borderRadius: 3,
                        border: "none",
                        background: enabled && !feature.comingSoon ? theme.color.accent : theme.color.borderStrong,
                        cursor: feature.comingSoon || toggling ? "not-allowed" : "pointer",
                        position: "relative",
                        transition: "background 0.15s",
                        opacity: toggling ? 0.6 : 1,
                      }}
                      aria-label={`${enabled ? "Disable" : "Enable"} ${feature.label}`}
                    >
                      <span style={{
                        position: "absolute",
                        top: 3,
                        left: enabled && !feature.comingSoon ? 23 : 3,
                        width: 18,
                        height: 18,
                        borderRadius: "50%",
                        background: "#fff",
                        transition: "left 0.15s",
                        boxShadow: "var(--c-sheetShadow)",
                      }} />
                    </button>
                  </div>

                  {/* Webhook URL input — shown when Teams notifications is enabled */}
                  {isTeamsNotif && enabled && (
                    <div style={{
                      padding: "12px 18px 14px",
                      borderBottom: i < arr.length - 1 ? `1px solid ${theme.color.border}` : "none",
                      background: theme.color.accentBg,
                    }}>
                      <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: theme.color.heading, marginBottom: 5 }}>
                        Teams Workflows webhook URL
                      </label>
                      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                        <input
                          style={{ ...inputStyle, flex: 1, minWidth: 260, fontSize: 12 }}
                          value={webhookUrl}
                          onChange={(e) => setWebhookUrl(e.target.value)}
                          placeholder="https://…environment.api.powerplatform.com/…"
                          onKeyDown={(e) => { if (e.key === "Enter") handleSaveWebhookUrl(); }}
                        />
                        <button
                          onClick={handleSaveWebhookUrl}
                          disabled={savingWebhook}
                          style={{ ...btnPrimary, padding: "7px 14px", opacity: savingWebhook ? 0.6 : 1 }}
                        >
                          {savingWebhook ? "Saving…" : "Save"}
                        </button>
                        {canAdmin && status!.features.teamsWebhookUrl && (
                          <button
                            onClick={handleTestTeams}
                            disabled={testingTeams}
                            style={{ ...btnPrimary, padding: "7px 14px", opacity: testingTeams ? 0.6 : 1 }}
                          >
                            {testingTeams ? "Sending…" : "Send test message"}
                          </button>
                        )}
                      </div>
                      <div style={{ fontSize: 11, color: theme.color.faint, marginTop: 5, lineHeight: 1.5 }}>
                        In Teams, open the channel's menu, choose Workflows, pick "Post to a channel when a webhook request is received", and paste the URL it gives you here.
                      </div>
                      {status!.features.teamsWebhookKind === "connector" && (
                        <div role="status" style={{ fontSize: 11, color: theme.color.warningText, background: theme.color.warningBg, border: `1px solid ${theme.color.warning}`, borderRadius: 3, padding: "6px 10px", marginTop: 8, lineHeight: 1.5 }}>
                          This is an Office 365 Connector webhook. Microsoft is retiring these; create a Workflows webhook for the channel and replace it before it stops working.
                        </div>
                      )}
                      {status!.features.teamsHealth?.lastError ? (
                        <div role="alert" style={{ fontSize: 11, color: theme.color.dangerText, background: theme.color.dangerBg, border: `1px solid ${theme.color.danger}`, borderRadius: 3, padding: "6px 10px", marginTop: 8, lineHeight: 1.5 }}>
                          Teams refused the last message ({when(status!.features.teamsHealth.lastErrorAt)}): {status!.features.teamsHealth.lastError}
                        </div>
                      ) : status!.features.teamsHealth?.lastDeliveredAt ? (
                        <div style={{ fontSize: 11, color: theme.color.successText, marginTop: 6 }}>
                          Last delivered {when(status!.features.teamsHealth.lastDeliveredAt)}.
                        </div>
                      ) : null}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Document sync info */}
          <div style={card}>
            <div style={{ fontSize: 13, fontWeight: 700, color: theme.color.heading, marginBottom: 8 }}>Document sync</div>
            <ul style={{ margin: 0, paddingLeft: 18, display: "flex", flexDirection: "column", gap: 5 }}>
              <li style={{ fontSize: 13, color: theme.color.subtle }}>Generated policy documents (.md files)</li>
              <li style={{ fontSize: 13, color: theme.color.subtle }}>Uploaded evidence documents</li>
            </ul>
          </div>
        </>
      )}

      {/* ── Disconnected state ── */}
      {!loadingStatus && !status?.connected && (
        <>
          <div style={{ marginBottom: 24 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: theme.color.heading, marginBottom: 14 }}>What you get when connected</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 14 }}>
              {FEATURES.map((f) => (
                <div key={f.title} style={card}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: theme.color.heading, marginBottom: 6 }}>{f.title}</div>
                  <div style={{ fontSize: 12, color: theme.color.subtle, lineHeight: 1.7 }}>{f.description}</div>
                </div>
              ))}
            </div>
          </div>

          <div style={card}>
            {/* Folder name input */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: theme.color.heading, marginBottom: 6 }}>
                Folder name in OneDrive
              </label>
              <input
                style={{ ...inputStyle, maxWidth: 320 }}
                value={folderName}
                onChange={(e) => setFolderName(e.target.value)}
                placeholder="Abacas Security Compliance Platform"
              />
              <div style={{ fontSize: 11, color: theme.color.faint, marginTop: 4 }}>
                This folder will be created in the root of your OneDrive for Business.
              </div>
            </div>

            <button
              onClick={handleConnect}
              disabled={connecting || !folderName.trim()}
              style={{ ...btnPrimary, display: "block", width: "100%", padding: "11px 18px", fontSize: 14, opacity: (connecting || !folderName.trim()) ? 0.7 : 1, marginBottom: 14 }}
            >
              {connecting ? "Redirecting to Microsoft…" : "Connect Microsoft 365"}
            </button>
            <p style={{ margin: 0, fontSize: 12, color: theme.color.subtle, lineHeight: 1.7 }}>
              You'll be asked to sign in to your Microsoft 365 account and approve access. We request permission
              to read and write files in your chosen OneDrive folder and to create calendar events on your behalf.
              No email or other data is accessed.
            </p>
          </div>
        </>
      )}
    </div>
  );
}
